"use client";

import { useEffect } from "react";

import { useGetHostingUsageQuery } from "@/features/order/orderApiInjection";

type HostingUsage = {
	purchasedHostingEligibleServiceCount: number;
	activeHostingTier: "starter" | "growth" | "pro" | null;
};

/**
 * Live hosting-eligible site count for service page / basket waiver logic.
 * Refetches when the page mounts and when the tab regains focus (e.g. after admin marks
 * an order line as no longer needing hosting).
 */
export function useHostingUsage(enabled: boolean) {
	const query = useGetHostingUsageQuery(undefined, {
		skip: !enabled,
		refetchOnMountOrArgChange: true,
		refetchOnReconnect: true,
	});

	const { refetch } = query;

	useEffect(() => {
		if (!enabled) return;

		const refetchIfVisible = () => {
			if (document.visibilityState === "visible") {
				void refetch();
			}
		};

		document.addEventListener("visibilitychange", refetchIfVisible);
		window.addEventListener("focus", refetchIfVisible);

		return () => {
			document.removeEventListener("visibilitychange", refetchIfVisible);
			window.removeEventListener("focus", refetchIfVisible);
		};
	}, [enabled, refetch]);

	return query as typeof query & { data: HostingUsage | undefined };
}
